import React, { useEffect, useState } from "react";
import MainPage from "../layout/MainPage";
import { BrowserRouter } from "react-router-dom";
import { ThemeProvider, } from "@mui/material";
import colorTheme from "../assets/colorTheme";
import PageLoad from "./PageLoad";
import { useDispatch } from "react-redux";
import { setLoadingState, setNeedSetupRedirect, setIsASO } from "../state/appSlice";
import OriginService from "../services/originService";
import { useActions } from "../hooks/useActions";
import AdminSetupModal from "./AdminSetupModal";
import { useTypedSelector } from "../hooks/useTypedSelector";
import ElliHost from "../services/elliHost";
import LandingErrorModal from "../pages/LandingError";
import SessionExpirationModal from "./SessionExpirationModal";
import { useUserActivity } from "../hooks/useUserActivity";
import { INACTIVITY_WARNING_SECONDS, WARNING_MODAL_DURATION_SECONDS } from "../utils/constants";
import { LaunchDarklyProvider, useFeatureFlag, FeatureFlags } from "../featureFlags";

const AppContent = () => {
    const origin = OriginService.getInstance();

    // Feature Flag Testing - Remove after verification
    const isEnhancedLoggingEnabled = useFeatureFlag(FeatureFlags.UI_ENHANCED_LOGGING_ENABLED);

    useEffect(() => {
        const env = process.env.REACT_APP_ENVIRONMENT;
        if (env === 'DEV' || env === 'QA' || env === 'CONCEPT-DEV') {
            console.log('[FeatureFlag] ui-enhanced-logging-enabled:', isEnhancedLoggingEnabled);
        }
    }, [isEnhancedLoggingEnabled]);

    const { getConfigPlugInData } = useActions();
    const dispatch = useDispatch();
    const [adminModalOpen, setAdminModalOpen] = useState(false);
    const [landingModalOpen, setLandingModalOpen] = useState(false);
    const [adminModalText, setAdminModalText] = useState("");
    const [landingModalText, setLandingModalText] = useState("");
    const [isErrorState, setIsErrorState] = useState(false);
    const [sessionExpirationModalOpen, setSessionExpirationModalOpen] = useState(false);
    const [secondsRemaining, setSecondsRemaining] = useState(WARNING_MODAL_DURATION_SECONDS);
    const [isRefreshing, setIsRefreshing] = useState(false);

    const appLoading = useTypedSelector((state) => state.appSlice.loadingState)
    const isAdmin = useTypedSelector((state) => state.appSlice.isAdmin)
    const isASO = useTypedSelector((state) => state.appSlice.isASO)
    const configPluginData = useTypedSelector((state) => state.configPlugInData.data?.Configuration);
    const tpoError = useTypedSelector((state) => state.tpoError.error);
    const expirationDateTimeUTC = useTypedSelector((state) => state.appSlice.expirationDateTimeUTC);
    const accessToken = useTypedSelector((state) => state.appSlice.accessToken);

    const handleCloseModal = () => {
        const elliHost = ElliHost.getInstance();
        elliHost.closeApplication();
    };

    const handleSessionExpire = () => {
        setSessionExpirationModalOpen(false);
        const elliHost = ElliHost.getInstance();
        elliHost.closeApplication();
    };

    // Track user activity for inactivity-based modal
    // When user is inactive for INACTIVITY_WARNING_SECONDS, always show the session expiration modal
    // refreshOriginData() fetches a new token from Encompass using partnerAccessToken,
    // so it will succeed even if the frontend token has expired
    const { resetActivity } = useUserActivity({
        inactivityThresholdSeconds: INACTIVITY_WARNING_SECONDS,
        onInactive: () => {
            // Always show modal when user is inactive - user can click "Stay Logged In" to get a new token
            setSecondsRemaining(WARNING_MODAL_DURATION_SECONDS);
            setSessionExpirationModalOpen(true);
        },
        enabled: !!accessToken && !!expirationDateTimeUTC && !appLoading.loading
    });

    // Handle "Stay Logged In" button click - refresh token and reset activity tracking
    const handleSessionRefresh = async () => {
        if (isRefreshing) return; // Prevent multiple calls
        
        setIsRefreshing(true);
        try {
            await origin.refreshOriginData();
            setSessionExpirationModalOpen(false);
            // Reset activity tracking after successful refresh
            resetActivity();
        } catch (error) {
            console.error('Failed to refresh session:', error);
            // If refresh fails, close the application
            handleSessionExpire();
        } finally {
            setIsRefreshing(false);
        }
    };

    // Check URL query string for 'aso' using URLSearchParams (case-insensitive)
    useEffect(() => {
        const href = window.location.href.toLowerCase();
        const query = href.split('/');
        const hasASO = query.length > 0 && (query.includes('aso?') || query.includes('aso&') || query.includes('aso'));
        dispatch(setIsASO(hasASO));
    }, [dispatch]);

    useEffect(() => {
        if (isASO) {
            dispatch(setLoadingState({ loading: false }));
            // For ASO flows, short-circuit the standard app initialization and exit this effect early.
            return;
        } 

        const buildAppStart = async () => {
            dispatch(setLoadingState({ loading: true }));
            const originResponse = await origin.buildOrigin();

            if (originResponse.data.result.oAuthToken.accessToken && originResponse.data.result.configId) {
                if (originResponse.data.result.configId !== localStorage.getItem("configurationId")) {
                    localStorage.setItem('configurationId', originResponse.data.result.configId);
                }

                await getConfigPlugInData(originResponse.data.result.configId, originResponse.data.result.oAuthToken.accessToken);
            }
            else {
                setIsErrorState(true);
            }
        }

        //Not in Encompass EPC Window testing
        if (window.top === window.self) {
            dispatch(setLoadingState({ loading: true }));
            setTimeout(() => {
                dispatch(setLoadingState({ loading: false }));
            }, 3000)
            return;
        }

        buildAppStart()
            .catch(() => {
                setLandingModalOpen(true)
                setLandingModalText("Homepage failed to load. Contact Administrator.")
            })
            .finally(() => {
                dispatch(setLoadingState({ loading: false }));
            });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isASO])

    useEffect(() => {
        // Skip error/validation checks for ASO flow
        if (isASO) return;
        
        if (isErrorState) {
            if (isAdmin) {
                setAdminModalText("The OAuth credentials and configuration ID required for this service are missing or invalid. Please configure them in the Encompass Admin Services Management to proceed.");
                setAdminModalOpen(true);
            }
            else {
                setAdminModalText("Configuration missing credentials and/or ConfigID in Encompass Credential Manager.");
                setAdminModalOpen(true);
            }
        }
        else if (tpoError) {
            // Show error popup for TPO Connect validation errors
            setAdminModalText(tpoError);
            setAdminModalOpen(true);
        }
        else if (!appLoading.loading) {
            const hasPackageData = configPluginData?.Packages && configPluginData?.Packages.length > 0;
            const hasOtherControlsData = configPluginData?.OtherControls && configPluginData?.OtherControls.length > 0;

            if (!hasPackageData && !hasOtherControlsData) {
                if (!isAdmin) {
                    setAdminModalText("Current user is not an Admin user.");
                    setAdminModalOpen(true);
                } else
                    dispatch(setNeedSetupRedirect(true));
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [appLoading, isErrorState, tpoError, isASO]);

    return (
        <>
            <MainPage />
            {appLoading.loading && <PageLoad />}
            <AdminSetupModal isAdmin={isAdmin} open={adminModalOpen} onClose={handleCloseModal} text={adminModalText} />
            <LandingErrorModal open={landingModalOpen}
                onClose={handleCloseModal}
                modalText={landingModalText}
                modalTitle="Application Error"
                buttonText="Ok" />
            <SessionExpirationModal
                open={sessionExpirationModalOpen}
                secondsRemaining={secondsRemaining}
                onRefresh={handleSessionRefresh}
                onExpire={handleSessionExpire}
                isRefreshing={isRefreshing}
            />
        </>
    );
};

const App = () => {
    return (
        <React.Fragment>
            <LaunchDarklyProvider>
                <ThemeProvider theme={colorTheme}>
                    {/* react-redux subscribes via useSyncExternalStore, which forces sync
                        updates and cannot participate in a React transition. Opting out keeps
                        the v6 routing semantics this app was built against. */}
                    <BrowserRouter useTransitions={false}>
                        <AppContent />
                    </BrowserRouter>
                </ThemeProvider>
            </LaunchDarklyProvider>
        </React.Fragment>
    );
};

export default App;
